import {
  format,
  formatISO,
  formatDistanceToNowStrict,
  parse,
  parseISO,
} from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

export const regexOnlyLetters = /[^a-zà-úA-ZÀ-Ú ]/g;

export const regexOnlyNumbers = /[^0-9]/g;

export const regexCEP = /^[0-9]{5}-?[0-9]{3}$/;

export function formatCPF(value: string): string {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
}

export function formatCNPJ(value: string): string {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

export function formatCEP(value: string): string {
  return value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2');
}

export function formatPhone(value: string): string {
  // telefone fixo
  if (value.length === 10) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{4})/, '$1-$2');
  }

  // celular
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{4})/, '$1-$2');
}

export const formatTime = (value: string) => {
  return value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1:$2');
};

export const formatDate = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1');
};

export const formatDateToAPI = (value: string) => {
  const parsedDate = parse(value, 'P', new Date(), { locale: ptBR });
  return formatISO(parsedDate).toString();
};

export const formatDateToBr = (value: string) => {
  const parsedDate = parseISO(value);
  return format(parsedDate, 'dd/MM/yyyy').toString();
};

export const formatDateToATOM = (value: Date) => {
  return format(value, "yyyy-MM-dd'T'HH:mm:ssxxx").toString();
};

export const formatDateWithHour = (value: string) => {
  const parsedDate = parseISO(value);

  return format(parsedDate, "dd/MM/yyyy HH'h'mm").toString();
};

export const formatDateToShortString = (value: string | number) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  const formated = format(parsedDate, 'dd MMM yyyy', {
    locale: ptBR,
  }).toString();
  return formated;
};

export const formatDateToDayAndMonth = (value: string | number) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  const formated = format(parsedDate, 'dd MMM', {
    locale: ptBR,
  }).toString();
  return formated;
};

export const formatDateTimeToShortString = (
  value: string | number,
  separator: string,
  displayYear = true,
) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  const formated = format(
    parsedDate,
    `dd MMM${displayYear ? ' yyyy' : ''} '${separator}' HH:mm`,
    {
      locale: ptBR,
    },
  ).toString();
  return formated;
};

/**
 * Formada a data de acordo com o padrão solicitado
 *
 * @param date Data nos formatos ex.: "2023-05-05T08:07:36-03:00"
 * @param pattern padrão que deseja receber a data formatada
 */
export const formatDateToPatternRequested = (date: string, pattern: string) => {
  const parsedDate = parseISO(date);
  const innerPattern = pattern || "dd/MM/yyyy. 'Às' HH:mm";

  if (parsedDate.getFullYear() === 0) {
    return '';
  }

  return format(parsedDate, innerPattern, { locale: ptBR });
};

export const formatHour = (value: string, separator = 'h') => {
  const parsedDate = parseISO(value);

  return format(parsedDate, `HH'${separator}'mm`).toString();
};

export const formatDateDistance = (value: string, addSuffix = true) => {
  const parsedDate = parseISO(value);

  return formatDistanceToNowStrict(parsedDate, {
    locale: ptBR,
    addSuffix,
  });
};

export const formatPlate = (value: string) => {
  return value
    .replace(/[^0-9A-Z]/g, '')
    .replace(/([A-Z0-9]{3})([A-Z0-9]{4})/, '$1-$2');
};

export function plateOnlyDigits(plate: string): string {
  return plate
    .replace(/[\\(\\)\\.\s-]+/g, '')
    .replace(/[^\w\\-]+/g, '')
    .toUpperCase();
}

export function addDecimalPlace(value: string) {
  const middleZeroDecimalPlace = value.length === 1 ? '0' : '';

  return parseFloat(
    `${value.substring(
      0,
      value.length - 2,
    )}.${middleZeroDecimalPlace}${value.substring(
      value.length - 2,
      value.length,
    )}`,
  ).toFixed(2);
}

export function formatCurrency(value: number): string {
  const formatterCurrency = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatterCurrency.format(value);
}
